import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import {
  useGetProviderConfigsQuery,
  useDeleteProviderConfigMutation,
} from '../../../store/api/providerConfigsApi';
import type { ProviderConfig } from '../../../types/api';
import { formatDate } from '../../../utils/dateFormatter';

export default function ProviderConfigsListPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const { data, isLoading, error } = useGetProviderConfigsQuery();
  const [deleteProviderConfig, { isLoading: isDeleting }] = useDeleteProviderConfigMutation();

  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; providerId: string | null }>({
    isOpen: false,
    providerId: null,
  });

  const configs: ProviderConfig[] = data?.data?.configs || [];

  const handleDelete = async (providerId: string) => {
    try {
      await deleteProviderConfig(providerId).unwrap();
      toast.success(t('admin.providerConfigs.deleteSuccess') || 'Provider configuration deleted successfully');
      setDeleteDialog({ isOpen: false, providerId: null });
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to delete provider configuration');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.providerConfigs.error.title') || 'Error loading provider configurations'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.providerConfigs.title') || 'Provider Configurations'}
          </h1>
          <div className="flex items-center gap-3">
            {configs.length > 0 && (
              <div className="bg-brand-accent/20 border border-brand-accent/50 rounded-lg px-3 sm:px-4 py-2 w-fit">
                <span className="text-xs sm:text-sm font-medium text-neutral-medium">
                  {t('admin.providerConfigs.total') || 'Total Providers'}:{' '}
                </span>
                <span className="text-lg font-heading font-bold text-brand-accent">{configs.length}</span>
              </div>
            )}
            <Link
              to="/admin/provider-configs/new"
              className="px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
            >
              {t('admin.providerConfigs.addNew') || 'Add New Provider'}
            </Link>
          </div>
        </div>
        <p className="text-neutral-medium text-sm sm:text-base">
          {t('admin.providerConfigs.subtitle') || 'Manage provider configurations and settings'}
        </p>
      </div>

      {/* Configs Table (Desktop) */}
      {configs.length === 0 ? (
        <div
          className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-12 border border-neutral-dark/50 text-center"
          style={{
            animation: 'fadeIn 0.5s ease-out 0.2s both',
          }}>
          <p className="text-neutral-medium text-lg mb-4">
            {t('admin.providerConfigs.empty') || 'No provider configurations found'}
          </p>
          <Link
            to="/admin/provider-configs/new"
            className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            {t('admin.providerConfigs.addNew') || 'Add New Provider'}
          </Link>
        </div>
      ) : (
        <>
          <div
            className="hidden lg:block bg-neutral-dark/50 backdrop-blur-lg rounded-lg border border-neutral-dark/50 overflow-hidden transition-all duration-300 hover:border-brand-accent/30"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/80 border-b border-neutral-medium">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.providerConfigs.table.providerId') || 'Provider ID'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.providerConfigs.table.baseUrl') || 'Base URL'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.providerConfigs.table.margins') || 'Margins'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.providerConfigs.table.created') || 'Created'}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.providerConfigs.table.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-medium">
                  {configs.map((config, index) => (
                    <tr
                      key={config._id}
                      className="hover:bg-neutral-dark/50 transition-all duration-200 hover:shadow-lg hover:shadow-brand-accent/5"
                      style={{
                        animation: `fadeIn 0.4s ease-out ${index * 30}ms both`,
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm font-mono">
                        {config.providerId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm">
                        {config.endpoints?.baseUrl || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm">
                        {config.margins && Object.keys(config.margins).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(config.margins).map(([key, value]) => (
                              <span
                                key={key}
                                className="px-2 py-1 text-xs bg-brand-accent/20 text-brand-accent rounded border border-brand-accent/50"
                              >
                                {key}: {(value * 100).toFixed(1)}%
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-neutral-medium">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-medium text-sm">
                        {formatDate(config.createdAt, 'PP', currentLanguage)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/provider-configs/${config.providerId}`}
                            className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1"
                          >
                            {t('admin.providerConfigs.actions.view') || 'View'}
                            <svg
                              className="w-4 h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </Link>
                          <button
                            onClick={() => setDeleteDialog({ isOpen: true, providerId: config.providerId })}
                            className="text-red-400 hover:text-red-300 font-semibold transition-all duration-200 hover:underline"
                          >
                            {t('admin.providerConfigs.actions.delete') || 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Configs Cards (Mobile) */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
            {configs.map((config, index) => (
              <div
                key={config._id}
                className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 border border-neutral-dark/50 transition-all duration-200 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/5 animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-heading font-bold text-neutral-light font-mono">
                    {config.providerId}
                  </h3>
                  <Link
                    to={`/admin/provider-configs/${config.providerId}`}
                    className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1 text-sm"
                  >
                    {t('admin.providerConfigs.actions.view') || 'View'}
                    <svg
                      className="w-4 h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-medium font-medium">
                      {t('admin.providerConfigs.table.baseUrl') || 'Base URL'}:
                    </span>
                    <span className="text-neutral-light">{config.endpoints?.baseUrl || '-'}</span>
                  </div>
                  {config.margins && Object.keys(config.margins).length > 0 && (
                    <div>
                      <span className="text-neutral-medium font-medium mb-2 block">
                        {t('admin.providerConfigs.table.margins') || 'Margins'}:
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(config.margins).map(([key, value]) => (
                          <span
                            key={key}
                            className="px-2 py-1 text-xs bg-brand-accent/20 text-brand-accent rounded border border-brand-accent/50"
                          >
                            {key}: {(value * 100).toFixed(1)}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-medium font-medium">
                      {t('admin.providerConfigs.table.created') || 'Created'}:
                    </span>
                    <span className="text-neutral-light text-xs">
                      {formatDate(config.createdAt, 'PP', currentLanguage)}
                    </span>
                  </div>
                  <button
                    onClick={() => setDeleteDialog({ isOpen: true, providerId: config.providerId })}
                    className="w-full mt-2 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all duration-200 text-sm"
                  >
                    {t('admin.providerConfigs.actions.delete') || 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, providerId: null })}
        onConfirm={() => {
          if (deleteDialog.providerId) {
            void handleDelete(deleteDialog.providerId);
          }
        }}
        title={t('admin.providerConfigs.deleteTitle') || 'Delete Provider Configuration'}
        message={
          t('admin.providerConfigs.deleteConfirm') ||
          'Are you sure you want to delete this provider configuration? This action cannot be undone.'
        }
        confirmText={t('common.confirm') || 'Confirm'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

